import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ROUTES, STORAGE_KEYS } from '../utils/constants';
import { GoogleLogin } from '@react-oauth/google';
import api from '../services/api';
import SuccessModal from '../components/common/SuccessModal';

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('كلمة المرور غير متطابقة');
      return;
    }

    if (formData.password.length < 6) {
      setError('كلمة المرور يجب أن تكون 6 أحرف على الأقل');
      return;
    }

    setLoading(true);

    try {
      await register({
        name: formData.name,
        email: formData.email,
        password: formData.password
      });
      setShowSuccess(true);
      
      // Get redirect path from location state, default to dashboard
      const redirectTo = location.state?.from || ROUTES.DASHBOARD;
      
      setTimeout(() => {
        navigate(redirectTo);
      }, 2000);
    } catch (err) {
      setError(err.message || 'فشل التسجيل. حاول مرة أخرى');
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      setLoading(true);
      setError('');
      const response = await api.googleLogin(credentialResponse.credential, 'register');
      
      if (response.token && response.user) {
        localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, response.token);
        localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(response.user));
        setShowSuccess(true);
        
        // Get redirect path from location state, default to dashboard
        const redirectTo = location.state?.from || ROUTES.DASHBOARD;
        
        setTimeout(() => {
          window.location.href = redirectTo;
        }, 2000);
      }
    } catch (err) {
      setError(err.message || 'فشل التسجيل باستخدام Google');
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError('فشل التسجيل باستخدام Google. حاول مرة أخرى');
  };

  return (
    <div className="relative z-10 py-20 bg-bg min-h-screen flex items-center overflow-x-hidden">
      {showSuccess && (
        <SuccessModal message="مبروك! تم تسجيلك بنجاح في Allemni" />
      )}
      <div className="container mx-auto px-4">
        <div className="max-w-[500px] mx-auto">
          <div className="bg-white rounded-3xl shadow-custom-lg p-10">
            <div className="text-center mb-8">
              <div className="mx-auto mb-6 flex justify-center">
                <img 
                  src="/RIGHTALLEMNI.png" 
                  alt="Allemni - علمني" 
                  className="h-20 w-auto"
                />
              </div>
              <h1 className="text-3xl font-black text-text mb-2">انضم لـ Allemni!</h1>
              <p className="text-text3">سجل حسابك درك وابدأ التعلم</p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-xl text-red-600 text-center">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-text font-bold mb-2">الاسم الكامل</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-5 py-4 bg-bg border-2 border-border rounded-xl text-text outline-none focus:border-primary transition-colors"
                  placeholder="دخل اسمك الكامل"
                />
              </div>

              <div>
                <label className="block text-text font-bold mb-2">البريد الإلكتروني</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-5 py-4 bg-bg border-2 border-border rounded-xl text-text outline-none focus:border-primary transition-colors"
                  placeholder="example@email.com"
                />
              </div>

              <div>
                <label className="block text-text font-bold mb-2">كلمة المرور</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="w-full px-5 py-4 bg-bg border-2 border-border rounded-xl text-text outline-none focus:border-primary transition-colors"
                  placeholder="••••••••"
                />
              </div>

              <div>
                <label className="block text-text font-bold mb-2">تأكيد كلمة المرور</label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  className="w-full px-5 py-4 bg-bg border-2 border-border rounded-xl text-text outline-none focus:border-primary transition-colors"
                  placeholder="••••••••"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full px-8 py-4 bg-primary text-white font-bold rounded-xl hover:bg-primary-dark transition-all shadow-custom hover:shadow-custom-lg disabled:opacity-50"
              >
                {loading ? 'جاري التسجيل...' : 'إنشاء حساب'}
              </button>
            </form>

            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-text3">أو</span>
                </div>
              </div>

              <div className="mt-6 flex justify-center">
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={handleGoogleError}
                  text="signup_with"
                  locale="ar"
                  width="380"
                />
              </div>
            </div>

            <div className="mt-6 text-center">
              <p className="text-text3">
                عندك حساب مسبقاً؟{' '}
                <Link to={ROUTES.LOGIN} className="text-primary font-bold hover:underline">
                  سجل دخولك
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
